import { Asset } from 'shared-types';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

// Genormaliseerde state structuur
type AssetsState = {
  byId: Record<string, Asset>;
  allIds: string[];
  // Optioneel: tracking van laadstatus per asset
  loadingStatus: Record<string, 'idle' | 'loading' | 'success' | 'error'>;
};

type AssetsActions = {
  setAssets: (assets: Asset[]) => void;
  getAsset: (assetId: Asset['id']) => Asset | undefined;
  updateAssetPreview: (assetId: Asset['id'], previewImage: string) => void;
  updateAssetLoadingStatus: (
    assetId: Asset['id'],
    status: 'idle' | 'loading' | 'success' | 'error',
  ) => void;
};

// Gebruik de immer middleware voor veilige, directe mutaties
const useAssetsStore = create(
  subscribeWithSelector(
    immer<AssetsState & AssetsActions>((set, get) => ({
      byId: {},
      allIds: [],
      loadingStatus: {},

      setAssets: (assets: Asset[]) =>
        set((state) => {
          // Reset de state
          state.byId = {};
          state.allIds = [];
          state.loadingStatus = {};

          // Vul met nieuwe genormaliseerde data
          assets.forEach((asset) => {
            state.byId[asset.id] = asset;
            state.allIds.push(asset.id);
            state.loadingStatus[asset.id] = 'success';
          });
        }),

      getAsset: (assetId: Asset['id']) => {
        return get().byId[assetId];
      },

      updateAssetPreview: (assetId: Asset['id'], previewImage: string) =>
        set((state) => {
          // Controleer of de asset bestaat
          if (state.byId[assetId]) {
            // Directe mutatie is veilig met immer
            state.byId[assetId].previewImage = previewImage;
            state.loadingStatus[assetId] = 'success';
          }
        }),

      updateAssetLoadingStatus: (
        assetId: Asset['id'],
        status: 'idle' | 'loading' | 'success' | 'error',
      ) =>
        set((state) => {
          state.loadingStatus[assetId] = status;
        }),
    })),
  ),
);

// Helper selector om een array van assets terug te krijgen (compatibel met oudere code)
export const selectAllAssets = (state: AssetsState) => state.allIds.map((id) => state.byId[id]);

// Helper functie voor debugging in de console
if (typeof window !== 'undefined') {
  (window as any).debugStores = {
    ...((window as any).debugStores || {}),
    assets: {
      getState: () => useAssetsStore.getState(),
      subscribe: (callback: (state: AssetsState & AssetsActions) => void) =>
        useAssetsStore.subscribe(callback),
    },
  };
}

export default useAssetsStore;
